import { Controller } from '@hotwired/stimulus'

// Per-Editor-Preview-Buttons für Coworkr-Kampagnen:
// 1. Sammelt aktuelle Form-Werte (auch nicht gespeicherte)
// 2. Liest data-coworkr-preview-kind vom auslösenden Button (landingpage | newsletter | invite_push)
// 3. POSTet an /coworkr/campaigns/preview?kind=... und öffnet das Ergebnis im Modal-iframe
export default class extends Controller {
  static targets = ['form', 'modal', 'iframe', 'loading', 'title']
  static values = { url: String }

  connect() {
    if (!this.hasModalTarget) return

    // Modal & innere Elemente cachen, bevor wir das Modal aus dem Stimulus-Scope rausheben
    this._modal = this.modalTarget
    this._iframe = this._modal.querySelector('[data-coworkr-newsletter-preview-target="iframe"]')
    this._loading = this._modal.querySelector('[data-coworkr-newsletter-preview-target="loading"]')
    this._title = this._modal.querySelector('[data-coworkr-newsletter-preview-target="title"]')
    this._defaultTitle = this._title?.textContent || ''

    // An body anhängen, damit das Modal nicht im Stacking-Context von .container-fluid / navbar gefangen ist
    if (this._modal.parentNode !== document.body) {
      document.body.appendChild(this._modal)
      // data-action='click->...#close' feuert nicht mehr außerhalb des Controller-Scopes → manuell binden
      this._closeHandler = (e) => this.close(e)
      this._modal.querySelectorAll('[data-action*="#close"]').forEach(el => {
        el.addEventListener('click', this._closeHandler)
      })
    }
  }

  async open(event) {
    event.preventDefault()
    const trigger = event.currentTarget
    const kind = trigger?.dataset?.coworkrPreviewKind || 'newsletter'
    const titleText = trigger?.dataset?.coworkrPreviewTitle
    if (this._title && titleText) this._title.textContent = titleText
    else if (this._title) this._title.textContent = this._defaultTitle

    this.showModal()
    this._iframe.style.display = 'none'
    this._loading.style.display = ''

    try {
      // TipTap-TextEditor hält das hidden <input> bei jeder Tasteneingabe synchron —
      // FormData liest also stets den aktuellen Wert. Kein manueller Sync nötig.
      const formData = new FormData(this.formTarget)
      // Form hat method=PATCH → FormData enthält _method=patch. Rack::MethodOverride
      // würde sonst unseren POST /preview in PATCH /preview umwandeln → 404.
      formData.delete('_method')
      formData.append('kind', kind)

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.urlValue, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'text/html',
        },
        credentials: 'same-origin',
      })

      if (!response.ok) {
        throw new Error(`Preview failed: ${response.status}`)
      }

      const html = await response.text()
      this._iframe.srcdoc = html
      this._iframe.style.display = 'block'
      this._loading.style.display = 'none'
    } catch (error) {
      this._loading.innerHTML = `<div class="alert alert-danger">Vorschau fehlgeschlagen: ${error.message}</div>`
    }
  }

  close(event) {
    event?.preventDefault()
    this.hideModal()
    this._iframe.srcdoc = ''
  }

  showModal() {
    this._modal.classList.add('show')
    this._modal.style.display = 'block'
    this._modal.setAttribute('aria-hidden', 'false')
    document.body.classList.add('modal-open')
    this.ensureBackdrop()
  }

  hideModal() {
    this._modal.classList.remove('show')
    this._modal.style.display = 'none'
    this._modal.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('modal-open')
    this.removeBackdrop()
  }

  ensureBackdrop() {
    if (!this._backdrop) {
      this._backdrop = document.createElement('div')
      this._backdrop.className = 'modal-backdrop fade show'
      this._backdrop.addEventListener('click', () => this.close())
      document.body.appendChild(this._backdrop)
    }
  }

  removeBackdrop() {
    if (this._backdrop) {
      this._backdrop.remove()
      this._backdrop = null
    }
  }

  disconnect() {
    this.removeBackdrop()
    // Portaltes Modal aufräumen, sonst bleibt es bei Turbo-Navigation am body kleben
    if (this._modal && this._modal.parentNode === document.body) {
      this._modal.remove()
    }
  }
}
